<!-- AppTopbar dùng chung — Carot ERP v2 (module-rail.jsx :: AppTopbar, EP-UI-02 / US-UI-21):
     breadcrumb gốc = tên module · ô tìm ⌘K 280px mở MỘT bảng lệnh (CommandPaletteComponent,
     nhóm Thao tác nhanh + Điều hướng) · sáng/tối · chuông (chấm hồng, popover 360) · trợ
     giúp · khối tài khoản (tên + vai trò + avatar 28) ngăn bằng đường kẻ trái.
     Chiều cao 56px qua biến --shell-topbar-height (h-shell-topbar). -->
<header
  class="flex items-center h-shell-topbar px-5 gap-3 bg-warm-card border-b border-warm-border z-10"
  role="banner"
>
  <!-- Breadcrumb — `ds-breadcrumb` lo phần gập (>4 mục → đầu · … · hai mục cuối), dấu
       phân cách, cắt chữ và `aria-current`. Trước đây khối này là 110 dòng dựng tay ngay
       trong thanh, tức bản sao thứ hai của một component đã có; bản sao ấy là nơi duy nhất
       trong kho dựng breadcrumb, nên `ds-breadcrumb` chưa từng có người dùng thật.

       `queryParamsHandling="preserve"` KHÔNG phải tuỳ chọn: bối cảnh tổ chức đi theo query
       param, bỏ nó là ném người dùng về bối cảnh mặc định mà không báo gì. -->
  <ds-breadcrumb
    class="flex-1 min-w-0"
    [items]="breadcrumbItems"
    [maxVisible]="4"
    queryParamsHandling="preserve"
  />

  <!-- Bên phải: ô tìm + hành động -->
  <div class="flex items-center gap-1 shrink-0">
    <!-- Ô tìm — là NÚT mở bảng lệnh, không phải ô nhập. Bản cũ dùng `<input readonly>`:
         trình đọc màn hình công bố nó là ô nhập chữ, bấm Tab tới thì không gõ được gì, và
         nó không có `focus-visible` nào (adr/focus-visible-required báo lỗi ở đúng dòng đó).
         Bề rộng 280px của thiết kế qua biến --shell-search-width (w-shell-search). -->
    <button
      type="button"
      class="relative w-shell-search h-control-sm flex items-center gap-2 pl-8 pr-2 mr-1 rounded-md border border-warm-border bg-soft hover:border-strong transition-colors cursor-pointer focus-visible:outline-none focus-visible:shadow-focus"
      (click)="openCommandPalette()"
      [attr.aria-label]="'Layout.SearchOrCommand' | translate"
      [attr.aria-keyshortcuts]="commandPaletteAriaShortcut"
    >
      <span
        class="material-symbols-outlined absolute left-2.5 icon-size-token-xs text-warm-hint pointer-events-none"
        aria-hidden="true"
      >
        search
      </span>
      <span class="flex-1 text-left text-token-body-dense text-warm-hint truncate">
        {{ 'Layout.SearchOrCommand' | translate }}
      </span>
      <ds-kbd size="sm">{{ commandPaletteHint }}</ds-kbd>
    </button>

    <!-- Tỷ giá & giá vàng — chỉ hiện khi có quyền đọc danh mục mã thị trường, đúng quyền mà
         `currency/get-market-symbols` đòi; không thì nút mở ra một bảng 403. -->
    <app-fx-rates-button *hasPermission="'FINANCE.CURRENCY.VIEW'" />

    <!-- Đổi sáng/tối -->
    <ds-button
      variant="ghost"
      size="sm"
      [iconOnly]="true"
      [icon]="isDarkTheme ? 'dark_mode' : 'light_mode'"
      [ariaLabel]="isDarkTheme ? ('Layout.LightMode' | translate) : ('Layout.DarkMode' | translate)"
      [dsTooltip]="isDarkTheme ? ('Layout.LightMode' | translate) : ('Layout.DarkMode' | translate)"
      (click)="toggleTheme()"
    />

    <!-- Thông báo — chấm chưa đọc nằm trên thẻ bọc, không nằm trong nút: `ds-button` không
         có khe để chiếu nội dung chồng lên glyph. Chấm dùng màu nhấn theo thiết kế
         (`AppTopbar`: `background: c.accent`), không phải màu lỗi: một thông báo chưa đọc
         không phải một sự cố. -->
    <div class="relative">
      <ds-button
        variant="ghost"
        size="sm"
        [iconOnly]="true"
        icon="notifications"
        [ariaLabel]="'Layout.Notifications' | translate"
        [expanded]="notificationPanelOpen"
        (click)="notificationPanelOpen = !notificationPanelOpen"
      />
      @if (unreadCount > 0) {
        <span
          data-testid="notification-unread-dot"
          class="absolute top-1 right-1 w-2 h-2 bg-cta border-2 border-warm-card rounded-full pointer-events-none"
        ></span>
      }
      <notification-panel
        [notifications]="notifications"
        [isOpen]="notificationPanelOpen"
        [activeCategory]="activeCategory"
        (isOpenChange)="notificationPanelOpen = $event"
        (markAllRead)="notificationService.markAllRead()"
        (notificationClicked)="
          notificationService.navigateToAction($event); notificationPanelOpen = false
        "
        (dismissed)="notificationService.dismiss($event)"
        (snoozed)="notificationService.snooze($event.id, $event.minutes)"
        (categoryChanged)="notificationService.setCategory($event)"
        (viewAll)="notificationPanelOpen = false; notificationService.navigateToNotifications()"
      ></notification-panel>
    </div>

    <!-- Trợ giúp -->
    <ds-button
      variant="ghost"
      size="sm"
      [iconOnly]="true"
      icon="help"
      [ariaLabel]="'Layout.HelpCenter' | translate"
      [dsTooltip]="'Layout.HelpCenter' | translate"
    />

    <!-- Khối tài khoản — thiết kế ngăn bằng ĐƯỜNG KẺ TRÁI của chính khối, không phải một
         thẻ `<div>` gạch dọc rời (bản cũ: `h-6 w-[1px]`, một chiều rộng pixel tự chế). -->
    <div class="flex items-center border-l border-warm-border pl-2 ml-1">
      <button
        [dsMenuTriggerFor]="userMenu"
        type="button"
        class="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-warm-hover transition-colors cursor-pointer focus-visible:outline-none focus-visible:shadow-focus"
        [attr.aria-label]="'Layout.UserMenu' | translate"
      >
        <span class="text-right hidden sm:block">
          <span class="block text-token-caption font-semibold leading-tight text-warm-text">
            {{ currentUser?.name || currentUser?.userName }}
          </span>
          <span class="block text-token-label text-warm-hint leading-tight">
            {{ currentUser?.roleName || ('Layout.DefaultRole' | translate) }}
          </span>
        </span>
        <ds-avatar
          [name]="currentUser?.name || currentUser?.userName || ''"
          size="sm"
          tone="brand"
        />
      </button>
    </div>

    <ds-menu #userMenu position="below-end" minWidth="248px">
      <div class="px-4 py-3">
        <div class="font-semibold text-token-body-dense text-warm-text">
          {{ currentUser?.name || currentUser?.userName }}
        </div>
        <div class="text-token-caption text-warm-hint mt-0.5">
          {{ currentUser?.email }}
        </div>
      </div>
      <ds-menu-divider />
      <button ds-menu-item routerLink="/profile" [queryParamsHandling]="'preserve'">
        <span class="material-symbols-outlined mr-3 icon-size-token-sm text-warm-secondary">
          person
        </span>
        <span class="text-token-body-dense">{{ 'Profile.Title' | translate }}</span>
      </button>
      <button ds-menu-item (click)="toggleLanguage()">
        <span class="material-symbols-outlined mr-3 icon-size-token-sm text-warm-secondary">
          language
        </span>
        <span class="text-token-body-dense">{{ currentLanguageLabel }}</span>
      </button>
      <ds-menu-divider />
      <button ds-menu-item (click)="switchToMobileView()">
        <!-- v2: hồng chỉ cho CTA + số thông báo — mục menu dùng màu chữ thường. -->
        <span class="material-symbols-outlined mr-3 icon-size-token-sm text-warm-secondary">
          devices
        </span>
        <span class="text-token-body-dense font-medium text-warm-text flex-1">
          {{ 'Layout.MobileView' | translate }}
        </span>
        <span class="material-symbols-outlined icon-size-token-sm text-warm-hint">
          arrow_right_alt
        </span>
      </button>
      <ds-menu-divider />
      <button ds-menu-item (click)="logout()">
        <span class="material-symbols-outlined mr-3 icon-size-token-sm text-warm-secondary">
          logout
        </span>
        <span class="text-token-body-dense">{{ 'Button.Logout' | translate }}</span>
      </button>
    </ds-menu>
  </div>
</header>
